import { APP_ACTIVE_CONTEXT_KEY } from 'src/constants/sessionStorage';

export const APP_ACTIVE_CONTEXT_HEADER = 'X-Resq-App-Context';

export const isCustomerPortalPath = (pathname: string, basePath: string): boolean =>
    [`/${basePath.replace(/^\//, '').replace(/\/$/, '')}`, '/fast-pay-customer'].some(
        (path) => pathname === path || pathname.startsWith(`${path}/`),
    );

type StorageReader = Pick<Storage, 'getItem'>;
type StorageWriter = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;
type HeaderTarget = Headers | Record<string, string>;
type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type GraphqlServerErrorLike = {
    statusCode?: unknown;
    bodyText?: unknown;
};

export type AppActiveContext =
    | {
          type: 'vendor';
          vendorId: number;
          clientOrgId?: number;
      }
    | {
          type: 'client';
          clientOrgId: number;
          vendorId?: number;
          facilityId?: number;
      };

// Keep the selection available to requests even when browser persistence fails.
export const createAppContextStorage = (getStorage: () => StorageWriter): StorageWriter => {
    let value: string | null = null;
    let useMemory = false;
    return {
        getItem: (key) => {
            if (!useMemory) {
                try {
                    value = getStorage().getItem(key);
                } catch {
                    useMemory = true;
                }
            }
            return value;
        },
        setItem: (key, nextValue) => {
            value = nextValue;
            try {
                getStorage().setItem(key, nextValue);
                useMemory = false;
            } catch {
                useMemory = true;
            }
        },
        removeItem: (key) => {
            value = null;
            try {
                getStorage().removeItem(key);
                useMemory = false;
            } catch {
                useMemory = true;
            }
        },
    };
};

const tabContextStorage = createAppContextStorage(() => globalThis.sessionStorage);
const getDefaultStorage = (): StorageWriter => tabContextStorage;

const isContextId = (value: unknown): value is number =>
    Number.isSafeInteger(value) && Number(value) > 0;

const parseOptionalContextId = (value: unknown): number | null | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }
    return isContextId(value) ? value : null;
};

export const parseAppActiveContext = (value: unknown): AppActiveContext | null => {
    let parsed = value;
    if (typeof value === 'string') {
        try {
            parsed = JSON.parse(value);
        } catch {
            return null;
        }
    }
    if (!parsed || typeof parsed !== 'object') {
        return null;
    }

    const candidate = parsed as Record<string, unknown>;
    if (candidate.customerId !== null && candidate.customerId !== undefined) {
        return null;
    }

    const vendorId = parseOptionalContextId(candidate.vendorId);
    const clientOrgId = parseOptionalContextId(candidate.clientOrgId);
    const facilityId = parseOptionalContextId(candidate.facilityId);
    if (vendorId === null || clientOrgId === null || facilityId === null) {
        return null;
    }

    if (candidate.type === 'vendor' && vendorId !== undefined) {
        if (facilityId !== undefined) return null;
        return {
            type: 'vendor',
            vendorId,
            ...(clientOrgId === undefined ? {} : { clientOrgId }),
        };
    }
    if (candidate.type === 'client' && clientOrgId !== undefined) {
        return {
            type: 'client',
            clientOrgId,
            ...(vendorId === undefined ? {} : { vendorId }),
            ...(facilityId === undefined ? {} : { facilityId }),
        };
    }
    return null;
};

export const readAppActiveContext = (
    storage: StorageWriter | null = getDefaultStorage(),
): AppActiveContext | null => {
    if (!storage) {
        return null;
    }
    try {
        const rawContext = storage.getItem(APP_ACTIVE_CONTEXT_KEY);
        if (rawContext === null) {
            return null;
        }
        const context = parseAppActiveContext(rawContext);
        if (!context) {
            storage.removeItem(APP_ACTIVE_CONTEXT_KEY);
        }
        return context;
    } catch {
        return null;
    }
};

export const storeAppActiveContext = (
    value: unknown,
    storage: StorageWriter | null = getDefaultStorage(),
): boolean => {
    const context = parseAppActiveContext(value);
    if (!storage) {
        return false;
    }
    try {
        if (!context) {
            storage.removeItem(APP_ACTIVE_CONTEXT_KEY);
            return false;
        }
        storage.setItem(APP_ACTIVE_CONTEXT_KEY, JSON.stringify(context));
        return true;
    } catch {
        return false;
    }
};

export const clearAppActiveContext = (
    storage: Pick<Storage, 'removeItem'> | null = getDefaultStorage(),
): void => {
    try {
        storage?.removeItem(APP_ACTIVE_CONTEXT_KEY);
    } catch {
        // Tab storage may be unavailable in privacy-restricted browser contexts.
    }
};

export const recoverInvalidAppActiveContext = (
    storage: Pick<Storage, 'removeItem'> | null = getDefaultStorage(),
    reload: () => void = () => window.location.reload(),
): void => {
    clearAppActiveContext(storage);
    reload();
};

export const recoverInvalidAppActiveContextResponse = async (
    response: Response,
    storage: Pick<Storage, 'removeItem'> | null = getDefaultStorage(),
    reload: () => void = () => window.location.reload(),
): Promise<boolean> => {
    if (response.status !== 403) {
        return false;
    }
    try {
        const body = (await response.clone().json()) as { detail?: unknown };
        if (body.detail !== 'Invalid active context.') {
            return false;
        }
    } catch {
        return false;
    }
    recoverInvalidAppActiveContext(storage, reload);
    return true;
};

export const fetchWithAppActiveContextRecovery = async (
    input: RequestInfo | URL,
    init?: RequestInit,
    fetcher: Fetcher = globalThis.fetch,
): Promise<Response> => {
    const response = await fetcher(input, init);
    if (await recoverInvalidAppActiveContextResponse(response)) {
        throw new Error('Invalid active context.');
    }
    return response;
};

export const isInvalidAppActiveContextGraphqlError = (error: GraphqlServerErrorLike): boolean => {
    if (error.statusCode !== 403 || typeof error.bodyText !== 'string') {
        return false;
    }
    try {
        const body = JSON.parse(error.bodyText) as { detail?: unknown };
        return body.detail === 'Invalid active context.';
    } catch {
        return false;
    }
};

export const getAppActiveContextHeader = (
    storage: StorageReader | null = getDefaultStorage(),
): string | null => {
    // Customer-portal requests use their own selection in the server session.
    // Check at request time so client-side navigation also changes the scope.
    if (
        typeof window !== 'undefined' &&
        isCustomerPortalPath(
            window.location?.pathname ?? '',
            (window as Window & { ENV?: { RESQ_LITE_BASE_PATH?: string } }).ENV
                ?.RESQ_LITE_BASE_PATH ?? '/lite',
        )
    ) {
        return null;
    }
    if (!storage) {
        return null;
    }
    try {
        const context = parseAppActiveContext(storage.getItem(APP_ACTIVE_CONTEXT_KEY));
        return context ? JSON.stringify(context) : null;
    } catch {
        return null;
    }
};

export const applyAppActiveContextHeader = (
    headers: HeaderTarget,
    storage: StorageReader | null = getDefaultStorage(),
): void => {
    const value = getAppActiveContextHeader(storage);
    if (!value) {
        return;
    }
    if ('set' in headers && typeof headers.set === 'function') {
        headers.set(APP_ACTIVE_CONTEXT_HEADER, value);
        return;
    }
    (headers as Record<string, string>)[APP_ACTIVE_CONTEXT_HEADER] = value;
};
